if (!customElements.get("brand-logo-marquee")) {
  class BrandLogoMarquee extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.swiper = null;
      this.observer = null;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized || this.observer) return;

      this.slider = this.querySelector("[data-brand-logo-marquee]");

      if (!this.slider) return;

      const slides = this.querySelectorAll(".swiper-slide");

      if (slides.length < 2) {
        this.classList.add("is-static");
        return;
      }

      if ("IntersectionObserver" in window && !this.isThemeEditor()) {
        this.observe();
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.initialized = false;
      this.classList.remove("is-initialized");
    }

    observe() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.observer?.disconnect();
            this.observer = null;
            this.init();
          });
        },
        {
          rootMargin: "0px",
          threshold: 0.01,
        },
      );

      this.observer.observe(this);
    }

    async init() {
      if (this.initialized || !this.slider) return;

      try {
        await this.loadSwiper();

        if (!window.Swiper) {
          console.warn("[brand-logo-marquee] Swiper is not available.");
          return;
        }

        this.swiper = new window.Swiper(this.slider, this.getOptions());

        let hasMoved = false;
        let pointerStartX = 0;

        this.slider.addEventListener("pointerdown", (e) => {
          hasMoved = false;
          pointerStartX = e.clientX;
        });

        this.slider.addEventListener("pointermove", (e) => {
          if (!hasMoved && Math.abs(e.clientX - pointerStartX) > 10) {
            hasMoved = true;
          }
        });

        this.swiper.on("touchEnd", () => {
          if (hasMoved) return;

          requestAnimationFrame(() => {
            if (!this.swiper || this.swiper.destroyed) return;

            const currentTranslate = this.swiper.getTranslate();
            const slideSize =
              this.swiper.slidesSizesGrid[this.swiper.activeIndex] || 0;
            const spaceBetween = this.swiper.params.spaceBetween || 0;
            const nextTranslate =
              currentTranslate - slideSize - spaceBetween;

            this.swiper.setTransition(this.getSpeed());
            this.swiper.setTranslate(nextTranslate);

            this.swiper.wrapperEl.addEventListener(
              "transitionend",
              () => {
                if (!this.swiper || this.swiper.destroyed) return;
                this.swiper.loopFix();
                this.swiper.autoplay.stop();
                this.swiper.autoplay.start();
              },
              {once: true},
            );
          });
        });

        this.initialized = true;
        this.classList.add("is-initialized");
      } catch (error) {
        console.warn("[brand-logo-marquee] Failed to initialize slider:", error);
      }
    }

    getOptions() {
      return {
        slidesPerView: "auto",
        spaceBetween: 24,
        loop: true,
        speed: this.getSpeed(),
        allowTouchMove: true,
        grabCursor: true,
        touchReleaseOnEdges: true,
        autoplay: {
          delay: 0,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
        },
        freeMode: {
          enabled: true,
          momentum: true,
          momentumRatio: 0.6,
          momentumBounce: false,
        },
        watchOverflow: true,
        resizeObserver: true,
        observeParents: true,
        breakpoints: {
          750: {
            spaceBetween: 20,
          },
        },
      };
    }

    getSpeed() {
      const speed = parseFloat(this.dataset.speed || "");

      if (Number.isNaN(speed) || speed < 0) return 6000;

      return speed * 1000;
    }

    async loadSwiper() {
      if (window.Swiper) return;

      if (typeof window.loadSwiper === "function") {
        await window.loadSwiper();
        return;
      }

      if (window.__brandLogoMarqueeSwiperPromise) {
        return window.__brandLogoMarqueeSwiperPromise;
      }

      window.__brandLogoMarqueeSwiperPromise = Promise.all([
        this.loadStylesheet(this.dataset.swiperCss),
        this.loadScript(this.dataset.swiperJs),
      ]);

      return window.__brandLogoMarqueeSwiperPromise;
    }

    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error("Missing Swiper JS asset URL."));
          return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (existingScript) {
          if (existingScript.dataset.loaded === "true") {
            resolve();
            return;
          }

          existingScript.addEventListener("load", () => resolve(), {once: true});
          existingScript.addEventListener("error", reject, {once: true});
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;

        script.addEventListener(
          "load",
          () => {
            script.dataset.loaded = "true";
            resolve();
          },
          {once: true},
        );

        script.addEventListener("error", reject, {once: true});

        document.head.appendChild(script);
      });
    }

    loadStylesheet(href) {
      return new Promise((resolve) => {
        if (!href) {
          resolve();
          return;
        }

        const existingLink = document.querySelector(`link[href="${href}"]`);

        if (existingLink) {
          resolve();
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () => resolve();

        document.head.appendChild(link);
      });
    }

    isThemeEditor() {
      return window.Shopify && window.Shopify.designMode;
    }
  }

  customElements.define("brand-logo-marquee", BrandLogoMarquee);
}
